import { useCallback } from 'react';
import { ViewType } from '@audacy-clients/client-services/core';
import { createUseWatchableObjectValue } from '@audacy-clients/client-services/src/utils/createUseWatchableObject';
import WatchableObject from '@audacy-clients/client-services/src/utils/WatchableObject';
import type { ITabNavigation } from '@audacy-clients/core/atoms/config/navigation';
import { type IModule } from '@audacy-clients/core/atoms/wrappers/modules';
import { type IModuleProps } from '@audacy-clients/core/components/ModuleRenderer/types';

export const _defaultSubNavigationTabs: Array<ITabNavigation> = [
  {
    iconRef: 'Logo',
    label: 'For You',
    tabKey: `tab:${ViewType.MODULES}:home`,
    viewId: 'home',
    viewType: ViewType.MODULES,
  },
  {
    iconRef: 'Logo',
    label: 'Podcasts',
    tabKey: `tab:${ViewType.MODULES}:podcasts`,
    viewId: 'podcasts_index',
    viewType: ViewType.MODULES,
  },
  {
    iconRef: 'Logo',
    label: 'Music',
    tabKey: `tab:${ViewType.MODULES}:music`,
    viewId: 'music',
    viewType: ViewType.MODULES,
  },
  {
    iconRef: 'Logo',
    label: 'Sports',
    tabKey: `tab:${ViewType.MODULES}:sports`,
    viewId: 'sports',
    viewType: ViewType.MODULES,
  },
  {
    iconRef: 'Logo',
    label: 'News & Talk',
    tabKey: `tab:${ViewType.MODULES}:newstalk`,
    viewId: 'newstalk',
    viewType: ViewType.MODULES,
  },
];

type TSubnavigationConfiguration = {
  viewId: string;
  viewType: ViewType;
  route: string;
  module: IModule;
};

type TWatchableSubNavigationConfiguration = {
  subNavigationConfigurationMap: Map<string, TSubnavigationConfiguration>;
};

/**
 * SubNavigation configuration singleton
 * */
class WatchableSubNavigationConfiguration extends WatchableObject<TWatchableSubNavigationConfiguration> {
  constructor() {
    super({
      subNavigationConfigurationMap: new Map<string, TSubnavigationConfiguration>(),
    });
  }

  public setModuleInformation(props: IModuleProps) {
    const { viewId, viewType, module } = props;
    if (!(viewId && viewType && module.config.name)) {
      return;
    }
    const subNavigationConfiguration = {
      viewId,
      viewType,
      route: module.config.name,
      module,
    };
    const subNavigationConfigurationMap = this.getValue('subNavigationConfigurationMap');
    const existingConfig = subNavigationConfigurationMap.get(viewId);
    if (
      !existingConfig ||
      JSON.stringify(existingConfig) !== JSON.stringify(subNavigationConfiguration)
    ) {
      subNavigationConfigurationMap.set(viewId, subNavigationConfiguration);
      this.setValue('subNavigationConfigurationMap', subNavigationConfigurationMap);
    }
  }

  public reset() {
    this.setValue('subNavigationConfigurationMap', new Map<string, TSubnavigationConfiguration>());
  }
}

const internalSubNavigationConfiguration = Object.freeze(new WatchableSubNavigationConfiguration());

export function getSubNavigationConfigurationMap() {
  return internalSubNavigationConfiguration.getValue('subNavigationConfigurationMap');
}
export function resetSubNavigationConfiguration() {
  internalSubNavigationConfiguration.reset();
}

const watchableSubNavigationConfiguration = createUseWatchableObjectValue(
  internalSubNavigationConfiguration,
  'subNavigationConfigurationMap',
);

export function useWatchableSubNavigationConfiguration() {
  const [subNavigationConfigurationMap] = watchableSubNavigationConfiguration();
  const setSubNavigationModuleInformation = useCallback(
    internalSubNavigationConfiguration.setModuleInformation.bind(
      internalSubNavigationConfiguration,
    ),
    [],
  );
  return {
    subNavigationConfigurationMap,
    setSubNavigationModuleInformation,
  };
}
